import type { MetricDef } from '@nono/contract';
import { Download } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { KpiRail } from '@/components/data/KpiRail';
import { type MetricRow, MetricsTable } from '@/components/data/MetricsTable';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/ui/states';
import { downloadCsv } from '@/lib/export';
import { useFilters } from '@/lib/filters';
import { useT, useViewMessages } from '@/lib/i18n';
import { useFunnel, useMetrics } from '@/lib/queries';
import { BookingsKpi } from './BookingsKpi';
import { DayAds } from './DayAds';

// Headline columns for the blended 'all' view — a fitting funnel narrative
// (spend → bookings → LP% → show-up → SUR% → sold → CPA → revenue → ROI) so the
// default table fits without horizontal scroll. "Vis alle kolonner" reveals the
// rest. lead/booking already have few columns, so they show all decision metrics.
const HEADLINE_ALL = new Set([
  'adSpend',
  'bookings',
  'lpPct',
  'showedUp',
  'surPct',
  'soldMemberships',
  'newMembers',
  'recurringMembers',
  'cpa',
  'revenue',
  'rRoi',
]);

function visibleDefsFor(
  defs: MetricDef[],
  segment: 'all' | 'lead' | 'booking',
  showAll: boolean,
): MetricDef[] {
  if (showAll) return defs;
  if (segment === 'all') return defs.filter((d) => HEADLINE_ALL.has(d.key));
  return defs.filter((d) => d.decision);
}

function exportMetricsCsv(defs: MetricDef[], rows: MetricRow[], dateLabel: string): void {
  const headers = [dateLabel, ...defs.map((d) => d.label)];
  const csvRows: (string | number | null)[][] = rows.map((r) => [
    r.date,
    ...defs.map((d) => (typeof r[d.key] === 'number' ? (r[d.key] as number) : null)),
  ]);
  const first = rows[0]?.date ?? 'data';
  const last = rows[rows.length - 1]?.date ?? '';
  downloadCsv(`nono-oversikt-${first}_${last}.csv`, headers, csvRows);
}

const MESSAGES = {
  nb: {
    title: 'Oversikt',
    sub: 'Dagens beslutningssvar for betalt markedsføring.',
    tableTitle: 'Daglige tall',
  },
  en: {
    title: 'Overview',
    sub: "Today's decision answers for paid marketing.",
    tableTitle: 'Daily numbers',
  },
};

export function OverviewPage() {
  const t = useT();
  const m = useViewMessages(MESSAGES);
  const { segment } = useFilters();
  const [showAll, setShowAll] = useState(false);

  const metricsQuery = useMetrics();
  const funnelQuery = useFunnel();

  if (metricsQuery.isLoading)
    return (
      <PageFrame m={m}>
        <TableSkeleton />
      </PageFrame>
    );
  if (metricsQuery.isError || !metricsQuery.data)
    return (
      <PageFrame m={m}>
        <ErrorState onRetry={() => metricsQuery.refetch()} />
      </PageFrame>
    );

  const data = metricsQuery.data;
  const visibleDefs = visibleDefsFor(data.defs, data.segment, showAll);

  return (
    <PageFrame m={m}>
      {data.segment !== 'all' && data.segmentNote ? (
        <p className="flex items-start gap-2 rounded-md border border-border bg-surface px-3 py-2 text-label text-fg-muted">
          <CaveatRef id="segmentHardStop" />
          <span>{data.segmentNote}</span>
        </p>
      ) : null}

      <KpiRail metrics={data} funnel={funnelQuery.data} segment={segment} extra={<BookingsKpi />} />

      <section className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-section text-fg">{m.tableTitle}</h2>
            <Legend />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-10 sm:h-8"
              onClick={() => exportMetricsCsv(visibleDefs, data.rows as MetricRow[], t.date())}
            >
              <Download className="size-3.5" />
              CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-10 sm:h-8"
              onClick={() => setShowAll((s) => !s)}
            >
              {showAll ? t.showDecisionOnly() : t.showAllColumns()}
            </Button>
          </div>
        </div>
        {data.rows.length === 0 ? (
          <EmptyState title={t.noData()}>{t.noDataHint()}</EmptyState>
        ) : (
          <MetricsTable
            defs={visibleDefs}
            rows={data.rows as MetricRow[]}
            targets={data.targets}
            totals={(data.totals ?? null) as MetricRow | null}
            dateLabel={t.date()}
            totalsLabel={t.total()}
            renderExpansion={(row) => (
              <DayAds
                date={row.date}
                segment={segment}
                dayBookings={typeof row.bookings === 'number' ? row.bookings : null}
              />
            )}
          />
        )}
      </section>
    </PageFrame>
  );
}

function PageFrame({ m, children }: { m: (typeof MESSAGES)['nb']; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
      </header>
      {children}
    </div>
  );
}

function Legend() {
  const t = useT();
  return (
    <span className="hidden items-center gap-3 text-micro text-fg-subtle sm:flex">
      <LegendDot className="bg-good" label={t.legendGood()} />
      <LegendDot className="bg-warn" label={t.legendWarn()} />
      <LegendDot className="bg-bad" label={t.legendBad()} />
    </span>
  );
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`size-1.5 rounded-full ${className}`} />
      {label}
    </span>
  );
}
